import PropTypes from "prop-types";
import React, { useEffect, Suspense, lazy, useState } from "react";
import ScrollToTop from "./helpers/scroll-top";
import { BrowserRouter as Router, Switch, Route, Redirect } from "react-router-dom";
import { ToastProvider } from "react-toast-notifications";
import { multilanguage, loadLanguages } from "redux-multilanguage";
import { connect } from "react-redux";
import { BreadcrumbsProvider } from "react-breadcrumbs-dynamic";
import { useMemo } from "react";
import ContentContext from './context/ContentContext'
import controlData from "./data/contentCopy";
import axios from "axios";
import RenderCustomPage from "./pages/utility/RenderCustomPage";
import SuccessOrder from "./pages/other/SuccessOrder";
import Lightbulb from "./pages/other/Lighbulb";
import auth from "./helpers/auth";

// home pages
const HomeFashion = lazy(() => import("./pages/home/HomeFashion"));
const HomeFashionTwo = lazy(() => import("./pages/home/HomeFashionTwo"));
const HomeFashionThree = lazy(() => import("./pages/home/HomeFashionThree"));
const HomeFashionFour = lazy(() => import("./pages/home/HomeFashionFour"));
const HomeFashionFive = lazy(() => import("./pages/home/HomeFashionFive"));
const HomeFashionSix = lazy(() => import("./pages/home/HomeFashionSix"));
const HomeFashionSeven = lazy(() => import("./pages/home/HomeFashionSeven"));
const HomeFashionEight = lazy(() => import("./pages/home/HomeFashionEight"));
const HomeKidsFashion = lazy(() => import("./pages/home/HomeKidsFashion"));
const HomeCosmetics = lazy(() => import("./pages/home/HomeCosmetics"));
const HomeFurniture = lazy(() => import("./pages/home/HomeFurniture"));
const HomeFurnitureTwo = lazy(() => import("./pages/home/HomeFurnitureTwo"));
const DataStorage = lazy(() => import("./pages/utility/DataStorage"));
const HomeFurnitureThree = lazy(() =>
  import("./pages/home/HomeFurnitureThree")
);
const HomeFurnitureFour = lazy(() => import("./pages/home/HomeFurnitureFour"));
const HomeFurnitureFive = lazy(() => import("./pages/home/HomeFurnitureFive"));
const HomeFurnitureSix = lazy(() => import("./pages/home/HomeFurnitureSix"));
const HomeFurnitureSeven = lazy(() =>
  import("./pages/home/HomeFurnitureSeven")
);
const HomeElectronics = lazy(() => import("./pages/home/HomeElectronics"));
const HomeElectronicsTwo = lazy(() =>
  import("./pages/home/HomeElectronicsTwo")
);
const HomeElectronicsThree = lazy(() =>
  import("./pages/home/HomeElectronicsThree")
);
const HomeBookStore = lazy(() => import("./pages/home/HomeBookStore"));
const HomeBookStoreTwo = lazy(() => import("./pages/home/HomeBookStoreTwo"));
const HomePlants = lazy(() => import("./pages/home/HomePlants"));
const HomeFlowerShop = lazy(() => import("./pages/home/HomeFlowerShop"));
const HomeFlowerShopTwo = lazy(() => import("./pages/home/HomeFlowerShopTwo"));
const HomeOrganicFood = lazy(() => import("./pages/home/HomeOrganicFood"));
const HomeOrganicFoodTwo = lazy(() =>
  import("./pages/home/HomeOrganicFoodTwo")
);
const HomeOnepageScroll = lazy(() => import("./pages/home/HomeOnepageScroll"));
const HomeGridBanner = lazy(() => import("./pages/home/HomeGridBanner"));
const HomeAutoParts = lazy(() => import("./pages/home/HomeAutoParts"));
const HomeCakeShop = lazy(() => import("./pages/home/HomeCakeShop"));
const HomeHandmade = lazy(() => import("./pages/home/HomeHandmade"));
const HomePetFood = lazy(() => import("./pages/home/HomePetFood"));
const HomeMedicalEquipment = lazy(() =>
  import("./pages/home/HomeMedicalEquipment")
);
const HomeChristmas = lazy(() => import("./pages/home/HomeChristmas"));
const HomeBlackFriday = lazy(() => import("./pages/home/HomeBlackFriday"));
const HomeBlackFridayTwo = lazy(() =>
  import("./pages/home/HomeBlackFridayTwo")
);
const HomeValentinesDay = lazy(() => import("./pages/home/HomeValentinesDay"));

// shop pages
const ShopGridStandard = lazy(() => import("./pages/shop/ShopGridStandard"));
const ShopGridFilter = lazy(() => import("./pages/shop/ShopGridFilter"));
const ShopGridTwoColumn = lazy(() => import("./pages/shop/ShopGridTwoColumn"));
const ShopGridNoSidebar = lazy(() => import("./pages/shop/ShopGridNoSidebar"));
const ShopGridFullWidth = lazy(() => import("./pages/shop/ShopGridFullWidth"));
const ShopGridRightSidebar = lazy(() =>
  import("./pages/shop/ShopGridRightSidebar")
);
const ShopListStandard = lazy(() => import("./pages/shop/ShopListStandard"));
const ShopListFullWidth = lazy(() => import("./pages/shop/ShopListFullWidth"));
const ShopListTwoColumn = lazy(() => import("./pages/shop/ShopListTwoColumn"));

// product pages
const Product = lazy(() => import("./pages/shop-product/Product"));
const ProductEdit = lazy(() => import("./pages/shop-product/Product"));
const ProductTabLeft = lazy(() =>
  import("./pages/shop-product/ProductTabLeft")
);
const ProductTabRight = lazy(() =>
  import("./pages/shop-product/ProductTabRight")
);
const ProductSticky = lazy(() => import("./pages/shop-product/ProductSticky"));
const ProductSlider = lazy(() => import("./pages/shop-product/ProductSlider"));
const ProductFixedImage = lazy(() =>
  import("./pages/shop-product/ProductFixedImage")
);

// blog pages
const BlogStandard = lazy(() => import("./pages/blog/BlogStandard"));
const BlogNoSidebar = lazy(() => import("./pages/blog/BlogNoSidebar"));
const BlogRightSidebar = lazy(() => import("./pages/blog/BlogRightSidebar"));
const BlogDetailsStandard = lazy(() =>
  import("./pages/blog/BlogDetailsStandard")
);

// other pages
const About = lazy(() => import("./pages/other/About"));
const Contact = lazy(() => import("./pages/other/Contact"));
const Faq = lazy(() => import("./pages/other/Faq"));
const StoreLocation = lazy(() => import("./pages/other/SiteLocation"));
const MyAccount = lazy(() => import("./pages/other/MyAccount"));
const LoginRegister = lazy(() => import("./pages/other/LoginRegister"));
const RegisterComplete = lazy(() => import("./pages/other/RegisterComplete"));
const ForgotPassword = lazy(() => import("./pages/other/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/other/ResetPassword"));

const Cart = lazy(() => import("./pages/other/Cart"));
const Wishlist = lazy(() => import("./pages/other/Wishlist"));
const Compare = lazy(() => import("./pages/other/Compare"));
const Checkout = lazy(() => import("./pages/other/Checkout"));
const EditAddress = lazy(() => import("./pages/other/EditAddress"));
const NewAddress = lazy(() => import("./pages/other/NewAddress"));
const OrderDetails = lazy(() => import("./pages/other/OrderDetails"));
const MyOrderDetails = lazy(() => import("./pages/other/MyOrderDetail"));

const NotFound = lazy(() => import("./pages/other/NotFound"));
const ClosedShop = lazy(() => import("./pages/other/ShopClosed"))

const App = (props) => {
  const [productData, setProductData] = useState({});
  const [storeData, setStoreData] = useState();
  const [userData, setUserData] = useState();
  const [refreshUserData, setRefreshuserData] = useState();

  const [decorationData, setDecorationData] = useState([]);
  const [taxData, setTaxData] = useState([]);
  const [categories, setCategories] = useState();
  useEffect(() => {
    props.dispatch(
      loadLanguages({
        languages: {
          en: require("./translations/english.json"),
          fn: require("./translations/french.json"),
          de: require("./translations/germany.json")
        }
      })
    );
  });

  function getHeadersInPage() {
    const Authorization = `Bearer ${localStorage.getItem('access_token')}`;
    return { Authorization };
  }
  useEffect(() => {
    axios.get(`/api/auth/self`, { headers: getHeadersInPage() }).then(response => {
      setUserData(response?.data)
    }).catch(function (error) {
      if (error.response) {
        if(error.response.status !== 200) {
          localStorage.removeItem('access_token')
        }
      }
    })
  }, [refreshUserData]);

  const userDataListener = (data) => {
    setRefreshuserData(data)
  }

   useEffect(() => {
     fetch(`${window.location.origin}/api/promo/stores/domainsource`)
     .then(response => response.json())
     .then(response => {
      setStoreData(response);
     });
   }, []);
   useEffect(() => {
		fetch(`${window.location.origin}/api/promo/decoration-rule`)
			.then(response => response.json())
			.then(response => {
				setDecorationData(response.data.decorationRuleList);
			});
	}, []);
  useEffect(() => {
		fetch(`${window.location.origin}/api/promo/tax-rules`)
			.then(response => response.json())
			.then(response => {
				setTaxData(response.items);
			});
	}, []);
  useEffect(() => {
    const flatList = []
		fetch(`/api/promo/store/category/MasterCategory`)
			.then(response => response.json())
			.then(response => {
        let categories = response.data.categories
        	for (let i = 0; i < categories?.length; i += 1) {
          flatList.push({name: categories[i]?.name, decorationId: categories[i]?.decorationRule?.id, taxId: categories[i]?.taxRule?.id, qtyDisplay: categories[i]?.minimumQuantityDisplay, secondaryPrice: categories[i]?.secondaryPrice || false});
          for (let j = 0; j < categories[i]?.subCategory?.length; j += 1) {
            flatList.push({name: `${categories[i]?.name}/${categories[i]?.subCategory[j]?.name}`, decorationId: categories[i]?.subCategory[j]?.decorationRule?.id || categories[i]?.decorationRule?.id, taxId: categories[i]?.subCategory[j]?.taxRule?.id, qtyDisplay: categories[i]?.subCategory[j]?.minimumQuantityDisplay || categories[i]?.minimumQuantityDisplay, secondaryPrice: categories[i]?.subCategory[j]?.secondaryPrice || false});
            for (let k = 0; k < categories[i]?.subCategory[j]?.childCategory?.length; k += 1) {
              flatList.push({name: `${categories[i]?.name}/${categories[i]?.subCategory[j].name}/${categories[i]?.subCategory[j]?.childCategory[k]?.name}`, decorationId: categories[i]?.subCategory[j]?.childCategory[k]?.decorationRule?.id || categories[i]?.subCategory[j]?.decorationRule?.id || categories[i]?.decorationRule?.id , taxId: categories[i]?.subCategory[j]?.childCategory[k]?.taxRule?.id, qtyDisplay: categories[i]?.subCategory[j]?.childCategory[k]?.minimumQuantityDisplay || categories[i]?.subCategory[j]?.minimumQuantityDisplay || categories[i]?.minimumQuantityDisplay, secondaryPrice: categories[i]?.subCategory[j]?.childCategory[k]?.secondaryPrice || false });
			}
		}
	}
				setCategories(flatList);
			});
	}, []);
  if (storeData?.data?.storeBranding !== undefined) {
    document.documentElement?.style.setProperty('--accent-color', storeData.data.storeBranding.primaryColor); // eslint-disable-line no-unused-expressions
    document.documentElement?.style.setProperty('--text-color', storeData.data.storeBranding.secondaryColor); // eslint-disable-line no-unused-expressions
  }
  if(!storeData) {
    return (
    <div className="flone-preloader-wrapper">
      <div className="flone-preloader">
        <span></span>
        <span></span>
      </div>
    </div>
    )
  }
  if(!storeData?.data?.isActive) {
    return (
  <ContentContext.Provider value={{content: storeData?.data, storeData, decorationData, categories, taxData, userData, userDataListener}}>
  <ToastProvider placement="bottom-left">
    <BreadcrumbsProvider>
      <Router>
        <ScrollToTop>
          <Suspense
            fallback={
              <div className="flone-preloader-wrapper">
                <div className="flone-preloader">
                  <span></span>
                  <span></span>
                </div>
              </div>
            }
          >
            <Switch>
              <Route
                exact
                path={process.env.PUBLIC_URL + "/"}
                component={ClosedShop}
              />
              </Switch>
            </Suspense>
          </ScrollToTop>
        </Router>
      </BreadcrumbsProvider>
    </ToastProvider>
    </ContentContext.Provider>
    )
  }
  return (
    <ContentContext.Provider value={{content: storeData?.data, storeData, decorationData, categories, taxData, userData, userDataListener}}>
    <ToastProvider placement="bottom-left">
      <BreadcrumbsProvider>
        <Router>
          <ScrollToTop>
            <Suspense
              fallback={
                <div className="flone-preloader-wrapper">
                  <div className="flone-preloader">
                    <span></span>
                    <span></span>
                  </div>
                </div>
              }
            >
              <Switch>
                <Route
                  exact
                  path={process.env.PUBLIC_URL + "/"}
                  component={HomeFashionTwo}
                />

                {/* Homepages */}

                {/* Shop pages */}
                <Route
                  path={process.env.PUBLIC_URL + "/shop"}
                  component={ShopGridStandard}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/lightbulb"}
                  component={Lightbulb}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/pages/:componentUrl"}
                  component={RenderCustomPage}
                />

                {/* Shop product pages */}
                <Route
                  path={process.env.PUBLIC_URL + "/product/:id"}
                  render={(routeProps) => (
                    <Product {...routeProps} key={routeProps.match.params.id} />
                  )}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/edit/:id/:cartIndex"}
                  render={(routeProps) => (
                    <ProductEdit {...routeProps} key={routeProps.match.params.id} />
                  )}
                />


                {/* Other pages */}
                <Route
                  path={process.env.PUBLIC_URL + "/about"}
                  component={About}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/datastorage"}
                  component={DataStorage}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/contact"}
                  component={Contact}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/faq"}
                  component={Faq}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/store-location"}
                  component={StoreLocation}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/my-account"}
                  component={MyAccount}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/login-register"}
                  render={() => {
                    if (!auth.isAuthenticated()) {
                        return (
                            <LoginRegister
                            location = {{pathname:'/login-register'}} />
                        );
                    }
                    return (
                        <Redirect
                            to={{
                                pathname: '/',
                                state: { from: '/checkout' },
                            }}
                        />
                    );
                }}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/login-redirect/:page"}
                  render={() => {
                    if (!auth.isAuthenticated()) {
                        return (
                            <LoginRegister
                            location = {{pathname:'/login-register'}} />
                        );
                    }
                }}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/confirm-email"}
                  component={RegisterComplete}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/forgot-password"}
                  component={ForgotPassword}
                />
                  <Route
                  path={process.env.PUBLIC_URL + "/reset-password"}
                  component={ResetPassword}
                />

                <Route
                  path={process.env.PUBLIC_URL + "/cart"}
                  component={Cart}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/wishlist"}
                  component={Wishlist}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/compare"}
                  component={Compare}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/checkout"}
                  render={() => {
                    if (auth.isAuthenticated()) {
                        return (
                            <Checkout
                            location = {{pathname:'/checkout'}} />
                        );
                    }
                    return (
                        <Redirect
                            to={{
                                pathname: '/login-register',
                                state: { from: '/checkout' },
                            }}
                        />
                    );
                }}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/edit-address"}
                  component={EditAddress}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/new-address"}
                  component={NewAddress}
                />

                <Route
                path={process.env.PUBLIC_URL + "/success/:id"}
                component={SuccessOrder}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/order-details"}
                  component={OrderDetails}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/order/:id"}
                  component={MyOrderDetails}
                />

                <Route
                  path={process.env.PUBLIC_URL + "/not-found"}
                  component={NotFound}
                />

                <Route exact component={NotFound} />
              </Switch>
            </Suspense>
          </ScrollToTop>
        </Router>
      </BreadcrumbsProvider>
    </ToastProvider>
    </ContentContext.Provider>
  );
};

App.propTypes = {
  dispatch: PropTypes.func
};

export default connect()(multilanguage(App));
